"use client";

import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { formatPaymentAmount } from "../lib/format";

/** Divider-flanked caption used between blocks on a checkout screen. */
export function PaymentSectionHeading({ children }: { children: string }) {
  return (
    <div className="flex items-center gap-3 py-1">
      <div className="h-px flex-1 bg-border dark:bg-white/[0.14]" />
      <div className="text-[13px] font-semibold tracking-[0.24em] text-text-secondary uppercase dark:text-white/78">
        {children}
      </div>
      <div className="h-px flex-1 bg-border dark:bg-white/[0.14]" />
    </div>
  );
}

/**
 * Secondary "listed as ₹X" line under a converted amount. Renders nothing when
 * there is no source amount, so callers can pass optional forex fields straight
 * through.
 */
export function PaymentSourceAmountLabel({
  label = "Listed as",
  amountMinor,
  currency,
}: {
  label?: string;
  amountMinor: number | null | undefined;
  currency: string | null | undefined;
}) {
  if (amountMinor == null || !currency) return null;
  return (
    <div className="mt-0.5 text-xs text-text-tertiary dark:text-white/45">
      {label} {formatPaymentAmount(amountMinor, currency)}
    </div>
  );
}

/** Full-width status strip above the checkout body (hold countdown, notices). */
export function PaymentNoticeBanner({
  tone = "neutral",
  children,
}: {
  tone?: "neutral" | "error";
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "rounded-xl px-3 py-2 text-center text-sm font-medium",
        tone === "error"
          ? "bg-error-bg text-error dark:bg-red-500/18 dark:text-red-50"
          : "bg-surface-alt text-text-secondary dark:bg-glass-bg-08 dark:text-white/72",
      )}
    >
      {children}
    </div>
  );
}

/** Rounded card the checkout screens use for every content block. */
export function PaymentCard({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <section
      className={cn(
        "rounded-[20px] border border-border bg-surface p-4 shadow-sm dark:border-white/[0.12] dark:bg-white/[0.055] dark:shadow-none",
        className,
      )}
    >
      {children}
    </section>
  );
}
